// src/pages/OrderDetail/OrderDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { ordersAPI } from '../../api/orders.js';
import { useAuth } from '../../hooks/useAuth.js';
import { Breadcrumbs } from '../../components/common/Breadcrumbs/Breadcrumbs';
import { Loader } from '../../components/ui/Loader/Loader.jsx';
import { Button } from '../../components/ui/Button/Button.jsx';
import { formatDate, formatPrice } from '../../utils/formatters.js';
import { ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS } from '../../utils/constants.js';

// 🔑 ЧЕЛОВЕЧЕСКИЕ НАЗВАНИЯ. Было: в блоке «Детали заказа» печаталось
//    `post` и `card` — значения из базы, которые клиент не поймёт.
//    ⚠️ Словарь, а не перевод на месте: одно место правки, когда появятся
//    новые способы (Курьер, ТК, онлайн-оплата).
const DELIVERY_LABELS = {
  post: 'Почта России',
  pickup: 'Самовывоз (Старый Оскол)',
  courier: 'Курьером',
  delivery: 'Доставка транспортной компанией',
};
const PAYMENT_LABELS = {
  card: 'Оплата при получении / перевод',
  cash: 'Наличными при получении',
  online: 'Онлайн',
};

export const OrderDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadOrder();
  }, [id]);

  // Загрузка заказа с ДВУМЯ путями доступа.
  //
  // ⚠️ ПОЧЕМУ ТАК. `GET /orders/:id` закрыт `adminMiddleware` — он отдаёт
  //    заказ целиком (включая `admin_notes`), и открывать его гостю нельзя.
  //    Поэтому:
  //      1) если человек авторизован — пробуем как раньше;
  //      2) если нет (или админский путь отказал) — идём в публичный трекер
  //         `/orders/public/:id`, передавая email/телефон для подтверждения
  //         владения. Их положил Checkout в `location.state` сразу после
  //         оформления; при перезагрузке страницы state теряется — тогда
  //         покажем форму подтверждения (см. `needsProof`).
  const loadOrder = async () => {
    setIsLoading(true);
    const guestEmail = location.state?.guestEmail || '';
    const guestPhone = location.state?.guestPhone || '';
    try {
      if (isAuthenticated) {
        const response = await ordersAPI.getById(id);
        setOrder(response.data.data);
      } else {
        const response = await ordersAPI.getPublic(id, {
          email: guestEmail || undefined,
          phone: guestPhone || undefined,
        });
        setOrder(response.data.data);
      }
    } catch (error) {
      // Гость без подтверждения (или чужая попытка) — публичный маршрут
      // отвечает 404 намеренно, чтобы не подтверждать существование заказа.
      console.error('Error loading order:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loader text="Загрузка заказа..." />;
  }

  if (!order) {
    return (
      <div className="container" style={{ paddingTop: '60px', textAlign: 'center' }}>
        <h2>Заказ не найден</h2>
        <Link to="/orders">
          <Button style={{ marginTop: '16px' }}>Вернуться к заказам</Button>
        </Link>
      </div>
    );
  }

  const breadcrumbs = [
    { label: 'Мои заказы', path: '/orders' },
    { label: `Заказ #${order.order_number}`, path: `/orders/${order.id}` },
  ];

  return (
    <div style={{ backgroundColor: 'rgb(var(--c-bg))', minHeight: '100vh', paddingBottom: '40px' }}>
      <div className="container" style={{ paddingTop: '24px' }}>
        <Breadcrumbs items={breadcrumbs} />

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '24px',
          marginBottom: '32px',
          flexWrap: 'wrap',
          gap: '16px',
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 700,
            color: 'rgb(var(--c-text))',
          }}>
            Заказ #{order.order_number}
          </h1>
          <span style={{
            padding: '6px 16px',
            borderRadius: '100px',
            fontSize: '14px',
            fontWeight: 500,
            backgroundColor: order.status === 'completed' ? '#28A745' :
                          order.status === 'cancelled' ? '#DC3545' :
                          '#FFC107',
            color: 'rgb(var(--c-on-accent))',
          }}>
            {ORDER_STATUS_LABELS[order.status] || order.status}
          </span>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '32px',
        }}>
          {/* Информация о заказе */}
          <div>
            <div style={{
              backgroundColor: 'rgb(var(--c-surface))',
              border: '1px solid rgb(var(--c-border))',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px',
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 600,
                color: 'rgb(var(--c-text))',
                marginBottom: '16px',
              }}>
                {/* 🔑 ЗАГОЛОВОК ЧЕСТНЫЙ (2026-10-29): в заказе могут быть
                    только наклейки — тогда слово «Товары» над списком
                    наклеек вводит в заблуждение. */}
                Позиции заказа
              </h3>

              {/* 🔑 НАКЛЕЙКИ — ПЕРВЫМИ (2026-10-29). Поймано сквозным
                  тестом: у заказа из одних наклеек блок был ПУСТ, клиент
                  видел сумму 165 ₽ и ни одной позиции. */}
              {order.StickerOrderItems?.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '12px 0',
                    borderBottom: '1px solid rgb(var(--c-border))',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500, color: 'rgb(var(--c-text))' }}>
                      {item.title}
                    </div>
                    <div style={{ fontSize: '14px', color: 'rgb(var(--c-text-secondary))' }}>
                      Размер {Math.round(Number(item.size_mm) / 10)} см · кромка{' '}
                      {Number(item.edge_mm).toFixed(1)} мм · {item.quantity} × {formatPrice(item.unit_price)}
                    </div>
                  </div>
                  <div style={{ fontWeight: 600, color: 'rgb(var(--c-text))' }}>
                    {formatPrice(item.line_total)}
                  </div>
                </div>
              ))}

              {order.OrderItems?.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '12px 0',
                    borderBottom: '1px solid rgb(var(--c-border))',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500, color: 'rgb(var(--c-text))' }}>
                      {item.product_name}
                    </div>
                    <div style={{ fontSize: '14px', color: 'rgb(var(--c-text-secondary))' }}>
                      {item.quantity} × {formatPrice(item.unit_price)}
                    </div>
                  </div>
                  <div style={{ fontWeight: 600, color: 'rgb(var(--c-text))' }}>
                    {formatPrice(item.total_price)}
                  </div>
                </div>
              ))}
              {/* 🔑 ДОСТАВКА ОТДЕЛЬНОЙ СТРОКОЙ. У наклеек она 150 ₽
                  (Почта России), и без строки «Итого» не сходится с
                  суммой позиций — клиент решит, что его обсчитали. */}
              {Number(order.delivery_cost) > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '12px',
                  fontSize: '14px',
                  color: 'rgb(var(--c-text-secondary))',
                }}>
                  <span>Доставка</span>
                  <span>{formatPrice(order.delivery_cost)}</span>
                </div>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '2px solid rgb(var(--c-border))',
                fontSize: '18px',
                fontWeight: 700,
              }}>
                <span>Итого</span>
                <span>{formatPrice(order.total)}</span>
              </div>
            </div>

            {order.status_history && order.status_history.length > 0 && (
              <div style={{
                backgroundColor: 'rgb(var(--c-surface))',
                border: '1px solid rgb(var(--c-border))',
                borderRadius: '16px',
                padding: '24px',
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: 'rgb(var(--c-text))',
                  marginBottom: '16px',
                }}>
                  История статусов
                </h3>
                <div style={{ position: 'relative', paddingLeft: '24px' }}>
                  {order.status_history.map((history, index) => (
                    <div key={index} style={{ position: 'relative', paddingBottom: '16px' }}>
                      {index < order.status_history.length - 1 && (
                        <div style={{
                          position: 'absolute',
                          left: '-16px',
                          top: '24px',
                          width: '2px',
                          height: 'calc(100% - 16px)',
                          backgroundColor: 'rgb(var(--c-border))',
                        }} />
                      )}
                      <div style={{
                        position: 'absolute',
                        left: '-20px',
                        top: '4px',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: 'rgb(var(--c-accent))',
                      }} />
                      <div style={{ fontWeight: 500, color: 'rgb(var(--c-text))' }}>
                        {ORDER_STATUS_LABELS[history.status] || history.status}
                      </div>
                      <div style={{ fontSize: '14px', color: 'rgb(var(--c-text-secondary))' }}>
                        {formatDate(history.createdAt)}
                      </div>
                      {history.note && (
                        <div style={{ fontSize: '14px', color: 'rgb(var(--c-text-secondary))', marginTop: '4px' }}>
                          {history.note}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Детали */}
          <div>
            <div style={{
              backgroundColor: 'rgb(var(--c-surface))',
              border: '1px solid rgb(var(--c-border))',
              borderRadius: '16px',
              padding: '24px',
              position: 'sticky',
              top: '100px',
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 600,
                color: 'rgb(var(--c-text))',
                marginBottom: '16px',
              }}>
                Детали заказа
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: 'rgb(var(--c-text-secondary))', textTransform: 'uppercase' }}>
                    Дата заказа
                  </div>
                  <div style={{ fontWeight: 500 }}>{formatDate(order.createdAt)}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: 'rgb(var(--c-text-secondary))', textTransform: 'uppercase' }}>
                    Статус оплаты
                  </div>
                  <div style={{ fontWeight: 500 }}>
                    {PAYMENT_STATUS_LABELS[order.payment_status] || order.payment_status}
                  </div>
                </div>

                {order.delivery_address && (
                  <div>
                    <div style={{ fontSize: '12px', color: 'rgb(var(--c-text-secondary))', textTransform: 'uppercase' }}>
                      Адрес доставки
                    </div>
                    <div style={{ fontWeight: 500 }}>{order.delivery_address}</div>
                  </div>
                )}

                {order.delivery_method && (
                  <div>
                    <div style={{ fontSize: '12px', color: 'rgb(var(--c-text-secondary))', textTransform: 'uppercase' }}>
                      Способ доставки
                    </div>
                    <div style={{ fontWeight: 500 }}>{DELIVERY_LABELS[order.delivery_method] || order.delivery_method}</div>
                  </div>
                )}

                {order.payment_method && (
                  <div>
                    <div style={{ fontSize: '12px', color: 'rgb(var(--c-text-secondary))', textTransform: 'uppercase' }}>
                      Способ оплаты
                    </div>
                    <div style={{ fontWeight: 500 }}>{PAYMENT_LABELS[order.payment_method] || order.payment_method}</div>
                  </div>
                )}

                {order.customer_notes && (
                  <div>
                    <div style={{ fontSize: '12px', color: 'rgb(var(--c-text-secondary))', textTransform: 'uppercase' }}>
                      Комментарий
                    </div>
                    <div style={{ fontWeight: 500 }}>{order.customer_notes}</div>
                  </div>
                )}
              </div>

              {order.status === 'pending' && (
                <Button variant="secondary" fullWidth style={{ marginTop: '16px' }}>
                  Оплатить заказ
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};